import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import { getFollowedTournamentsUseCase, getPublicTournamentsUseCase } from '@/src/core/dependencies';
import { Tournament, TournamentFollowWithDetails } from '@/src/domain/models';
import { useTournamentFilters } from '@/src/presentation/hooks';
import { ExploreScreen, ExploreTab } from '@/src/presentation/screens/ExploreScreen';
import { useRouter } from 'expo-router';
import { useEffect, useState } from 'react';
import { logger } from '@/src/core/utils';

const PAGE_SIZE = 20;

export default function ExploreContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<ExploreTab>('discover');
  const [publicTournaments, setPublicTournaments] = useState<Tournament[]>([]);
  const [followedTournaments, setFollowedTournaments] = useState<TournamentFollowWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [followingLoading, setFollowingLoading] = useState(false);
  const [followingLoadingMore, setFollowingLoadingMore] = useState(false);
  const [followingHasMore, setFollowingHasMore] = useState(true);
  const [followingPage, setFollowingPage] = useState(0);

  const {
    searchQuery,
    setSearchQuery,
    filterStatus,
    setFilterStatus,
    filterType,
    setFilterType,
    sortBy,
    setSortBy,
    filteredTournaments,
    hasActiveFilters,
    clearFilters,
  } = useTournamentFilters(publicTournaments);

  useEffect(() => {
    loadPublicTournaments();
  }, []);

  useEffect(() => {
    if (activeTab === 'following' && user && followedTournaments.length === 0) {
      loadFollowedTournaments();
    }
  }, [activeTab, user]);

  const loadPublicTournaments = async () => {
    try {
      setLoading(true);
      setPage(0);
      setHasMore(true);
      const data = await getPublicTournamentsUseCase.execute(PAGE_SIZE, 0);
      setPublicTournaments(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch (error) {
      logger.error('Explore', 'Failed to load public tournaments', { error });
    } finally {
      setLoading(false);
    }
  };

  const loadMorePublicTournaments = async () => {
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const data = await getPublicTournamentsUseCase.execute(PAGE_SIZE, nextPage * PAGE_SIZE);

      if (data.length > 0) {
        setPublicTournaments((prev) => [...prev, ...data]);
        setPage(nextPage);
        setHasMore(data.length === PAGE_SIZE);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      logger.error('Explore', 'Failed to load more public tournaments', { error });
    } finally {
      setLoadingMore(false);
    }
  };

  const loadFollowedTournaments = async () => {
    if (!user) return;

    try {
      setFollowingLoading(true);
      setFollowingPage(0);
      setFollowingHasMore(true);
      const data = await getFollowedTournamentsUseCase.execute(user.id, PAGE_SIZE, 0);
      setFollowedTournaments(data);
      setFollowingHasMore(data.length === PAGE_SIZE);
    } catch (error) {
      logger.error('Explore', 'Failed to load followed tournaments', { error });
    } finally {
      setFollowingLoading(false);
    }
  };

  const loadMoreFollowedTournaments = async () => {
    if (followingLoadingMore || !followingHasMore || !user) return;

    try {
      setFollowingLoadingMore(true);
      const nextPage = followingPage + 1;
      const data = await getFollowedTournamentsUseCase.execute(user.id, PAGE_SIZE, nextPage * PAGE_SIZE);

      if (data.length > 0) {
        setFollowedTournaments((prev) => [...prev, ...data]);
        setFollowingPage(nextPage);
        setFollowingHasMore(data.length === PAGE_SIZE);
      } else {
        setFollowingHasMore(false);
      }
    } catch (error) {
      logger.error('Explore', 'Failed to load more followed tournaments', { error });
    } finally {
      setFollowingLoadingMore(false);
    }
  };

  const handleTournamentPress = (tournamentId: string) => {
    const tournament = publicTournaments.find(t => t.id === tournamentId)
      || followedTournaments.find(t => t.id === tournamentId);
    if (tournament && tournament.userId === user?.id) {
      router.push(`/tournament/${tournamentId}`);
    } else {
      router.push(`/public/${tournamentId}`);
    }
  };

  const handleTabChange = (tab: ExploreTab) => {
    setActiveTab(tab);
  };

  return (
    <ExploreScreen
      activeTab={activeTab}
      onTabChange={handleTabChange}
      isLoggedIn={!!user}
      filteredTournaments={filteredTournaments}
      followedTournaments={followedTournaments}
      loading={loading}
      loadingMore={loadingMore}
      followingLoading={followingLoading}
      followingLoadingMore={followingLoadingMore}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      filterStatus={filterStatus}
      setFilterStatus={setFilterStatus}
      filterType={filterType}
      setFilterType={setFilterType}
      sortBy={sortBy}
      setSortBy={setSortBy}
      hasActiveFilters={hasActiveFilters}
      clearFilters={clearFilters}
      filtersVisible={filtersVisible}
      setFiltersVisible={setFiltersVisible}
      handleTournamentPress={handleTournamentPress}
      onRefresh={activeTab === 'discover' ? loadPublicTournaments : loadFollowedTournaments}
      onLoadMore={activeTab === 'discover' ? loadMorePublicTournaments : loadMoreFollowedTournaments}
      colors={colors}
    />
  );
}
